/**
 * Copyright (c) 2025 Kalpit Patel
 *
 * Sole Developer and Owner:
 * Kalpit Patel is the sole developer and creator of this entire application.
 * No other individual or entity has contributed to the development of this software.
 * All rights reserved.
 *
 * This file is part of AksharJobs application.
 */

import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import ReactDOM from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faChartLine,
  faUsers,
  faBuilding,
  faUpload,
  faChartBar,
  faCog,
  faDownload,
  faUser,
  faBriefcase,
  faBullhorn,
  faCalendarAlt,
  faFileAlt,
  faEnvelope,
  faEdit,
  faTrash,
  faTimes,
  faSave,
  faMapMarkerAlt,
  faFilter,
  faClock,
  faEye,
  faCheckCircle,
  faTimesCircle,
  faPaperPlane
} from '@fortawesome/free-solid-svg-icons';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { buildApiUrl } from '../config/api';
import { useAuth } from '../context/AuthContext';
import ThemedLoadingSpinner from '../components/ThemedLoadingSpinner';
import AssociationDashboardHeader from '../components/AssociationDashboardHeader';
import StatCard from '../components/StatCard';
import AssociationSettings from './AssociationSettings';
import CommunityMemberBadge from '../components/CommunityMemberBadge';
import { Bar, Doughnut } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
} from 'chart.js';
import '../styles/UniversityDashboard.css';
import '../styles/RecruiterCandidateTracker.css';
import '../styles/AssociationDashboard.css';

// Custom styles to override global button padding for ngo dashboard
// and slightly tighten the members table layout
const ngoButtonStyles = `
  .ngo-dashboard-container .btn-primary,
  .ngo-dashboard-container .btn-secondary,
  .ngo-dashboard-container .btn-upload {
    padding: 8px 12px !important;
    font-size: 13px !important;
    width: auto !important;
    min-width: auto !important;
  }

  /* NGO members table layout tweaks */
  .ngo-members-table {
    table-layout: fixed;
    width: 100%;
  }

  .ngo-members-table th,
  .ngo-members-table td {
    padding: 10px 12px;
  }

  /* Give Activity and Jobs columns tighter widths so there is less empty space */
  .ngo-members-table th:nth-child(2),
  .ngo-members-table td:nth-child(2),
  .ngo-members-table th:nth-child(3),
  .ngo-members-table td:nth-child(3) {
    width: 120px;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleId = 'ngo-dashboard-button-styles';
  if (!document.getElementById(styleId)) {
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = ngoButtonStyles;
    document.head.appendChild(style);
  }
}

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

const NGODashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();

  // Check if this is BAPS Africa account
  const isBapsAfrica = user?.email === 'spco@africa.baps.org';

  // Check if this is ELAFA NGO (Elite Ability Foundation Africa)
  const ngoName = (user?.companyName || user?.ngoName || user?.associationName || '').toLowerCase();
  const isElafaNgo =
    ngoName.includes('elite ability foundation africa') ||
    ngoName.includes('elite ability foundation') ||
    ngoName.includes('elafa');

  // Initialize active section from URL so direct links don't reset to dashboard
  const initialSectionFromUrl = (() => {
    if (typeof window === 'undefined') return 'dashboard';
    const params = new URLSearchParams(window.location.search);
    return params.get('section') || 'dashboard';
  })();
  const [activeSection, setActiveSection] = useState(initialSectionFromUrl);
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const [dashboardData, setDashboardData] = useState({
    stats: {
      totalMembers: 0,
      onboardedMembers: 0,
      activeJobsForMembers: 0,
      placements: 0
    },
    profileCompletion: 0
  });

  const [members, setMembers] = useState([]);
  const [membersError, setMembersError] = useState('');
  const [memberSearch, setMemberSearch] = useState('');
  const [memberFilters, setMemberFilters] = useState({
    status: '',
    sector: '',
    location: '',
    activity: ''
  });
  const [selectedMember, setSelectedMember] = useState(null);
  const [memberActivity, setMemberActivity] = useState(null);
  const [showActivityModal, setShowActivityModal] = useState(false);
  const membersFetchedRef = useRef(false);

  const [bulkUploading, setBulkUploading] = useState(false);
  const [bulkUploadResult, setBulkUploadResult] = useState(null);

  // Announcements state
  const [announcements, setAnnouncements] = useState([]);
  const [announcementsError, setAnnouncementsError] = useState('');
  const announcementsFetchedRef = useRef(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [announcementForm, setAnnouncementForm] = useState({
    title: '',
    message: ''
  });

  // Events state
  const [events, setEvents] = useState([]);
  const [eventsError, setEventsError] = useState('');
  const eventsFetchedRef = useRef(false);
  const [showEventModal, setShowEventModal] = useState(false);
  const [editingEvent, setEditingEvent] = useState(null);
  const [eventForm, setEventForm] = useState({
    title: '',
    description: '',
    eventDate: '',
    eventTime: '',
    location: '',
    eventType: 'Meeting'
  });


  const navigationItems = useMemo(() => {
    const totalMembers = dashboardData.stats.totalMembers || 0;
    return [
      { id: 'dashboard', icon: faChartLine, label: 'Dashboard', section: 'dashboard' },
      {
        id: 'members',
        icon: faUsers,
        label: 'Members',
        section: 'members',
        badge: totalMembers > 0 ? String(totalMembers) : undefined
      },
      { id: 'jobs', icon: faBriefcase, label: 'Job Postings', section: 'jobs' },
      { id: 'announcements', icon: faBullhorn, label: 'Announcements', section: 'announcements' },
      { id: 'events', icon: faCalendarAlt, label: 'Events', section: 'events' },
      { id: 'bulk-upload', icon: faUpload, label: 'Bulk Upload', section: 'bulk-upload' },
      { id: 'analytics', icon: faChartBar, label: 'Analytics', section: 'analytics' },
      { id: 'myprofile', icon: faUser, label: 'My Profile', section: 'myprofile' },
      { id: 'settings', icon: faCog, label: 'Settings', section: 'settings' }
    ];
  }, [dashboardData.stats.totalMembers]);

  const location = useLocation();

  // Keep state in sync with URL changes (e.g., direct navigation/bookmarks)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const section = params.get('section');
    if (section && section !== activeSection) {
      setActiveSection(section);
    }
  }, [location.search, activeSection]);

  const handleNavClick = (item) => {
    if (item.section === 'myprofile') {
      navigate('/ngo-my-profile');
      return;
    }
    setActiveSection(item.section);
    // Persist section in URL for deep-linking without page reload
    navigate(`/ngo-dashboard?section=${item.section}`, { replace: true });
    if (window.innerWidth < 768) {
      setSidebarOpen(false);
    }
  };

  const getSectionTitle = () => {
    const titleMap = {
      'dashboard': 'Dashboard',
      'members': 'Members',
      'jobs': 'Job Postings',
      'announcements': 'Announcements',
      'events': 'Events',
      'bulk-upload': 'Bulk Upload',
      'analytics': 'Analytics',
      'myprofile': 'My Profile',
      'settings': 'Settings'
    };
    return titleMap[activeSection] || 'Dashboard';
  };

  const fetchDashboardData = useCallback(async () => {
    try {
      // Only show loading spinner for dashboard section
      // Other sections load immediately while data loads in background
      const shouldShowLoading = activeSection === 'dashboard' || activeSection === '';
      if (shouldShowLoading) {
        setLoading(true);
      }
      
      const token = localStorage.getItem('token');
      const statsResponse = await axios.get(
        buildApiUrl('/api/ngo/stats'),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      let profileCompletion = 0;
      try {
        const profileResponse = await axios.get(
          buildApiUrl('/api/user/profile'),
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (profileResponse.data) {
          const profile = profileResponse.data;
          const requiredFields = [
            'associationName',
            'officialEmail',
            'phoneNumber',
            'physicalAddress',
            'city',
            'county'
          ];
          const filledRequired = requiredFields.filter(field => {
            const value = profile[field];
            return value && value.toString().trim() !== '';
          }).length;

          profileCompletion = Math.round((filledRequired / requiredFields.length) * 100);
          if (profile.profileCompleted || profile.hasCompletedProfile) {
            profileCompletion = 100;
          }
        }
      } catch (profileError) {
        console.error('Error fetching association profile for completion:', profileError);
      }

      if (statsResponse.data?.success) {
        const stats = statsResponse.data.stats || {};
        setDashboardData(prev => ({
          ...prev,
          stats: {
            totalMembers: stats.totalMembers || 0,
            onboardedMembers: stats.onboardedMembers || 0,
            activeJobsForMembers: stats.activeJobsForMembers || 0,
            placements: stats.placements || 0
          },
          profileCompletion: Math.max(0, Math.min(100, profileCompletion))
        }));
      }
      
      if (shouldShowLoading) {
        setLoading(false);
      }
    } catch (error) {
      console.error('Error fetching association dashboard data:', error);
      if (activeSection === 'dashboard' || activeSection === '') {
        setLoading(false);
      }
    }
  }, [activeSection]);

  useEffect(() => {
    // Only fetch dashboard data when on dashboard section or on initial load
    // Other sections should load immediately without waiting for dashboard data
    if (activeSection === 'dashboard' || activeSection === '') {
      fetchDashboardData();
    }
  }, [activeSection]);

  const fetchMembers = useCallback(async (forceRefresh = false) => {
    // If we already have members and it's not a forced refresh, don't fetch again
    if (membersFetchedRef.current && !forceRefresh) {
      return;
    }
    
    // Never show loading - data loads in background
    setMembersError('');
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (memberFilters.status) params.append('status', memberFilters.status);
      if (memberFilters.sector) params.append('sector', memberFilters.sector);
      if (memberFilters.location) params.append('location', memberFilters.location);
      if (memberFilters.activity) params.append('activity', memberFilters.activity);
      
      const url = buildApiUrl('/api/ngo/members') + (params.toString() ? `?${params.toString()}` : '');
      const response = await axios.get(
        url,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setMembers(response.data.members || []);
        membersFetchedRef.current = true;
      } else {
        setMembers([]);
        setMembersError('Unable to load members right now.');
        membersFetchedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching association members:', error);
      setMembers([]);
      setMembersError(error.response?.data?.error || 'Failed to fetch members.');
      membersFetchedRef.current = true;
    }
  }, [memberFilters]);

  useEffect(() => {
    // Fetch if we're on members section
    if (activeSection === 'members') {
      // Force fetch when navigating to this section (ignore the ref to ensure fresh data)
      fetchMembers(true);
    } else {
      // Reset the ref when leaving members section so it can fetch fresh data when returning
      membersFetchedRef.current = false;
    }
  }, [activeSection, fetchMembers]);

  // Refetch members when filters change
  useEffect(() => {
    if (activeSection === 'members' && membersFetchedRef.current) {
      membersFetchedRef.current = false;
      fetchMembers(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberFilters.status, memberFilters.sector, memberFilters.location, memberFilters.activity]);

  const fetchAnnouncements = useCallback(async (forceRefresh = false) => {
    if (activeSection !== 'announcements') return;
    
    // If we already have announcements and it's not a forced refresh, don't fetch again
    if (announcementsFetchedRef.current && !forceRefresh) {
      return;
    }
    
    // Never show loading - data loads in background
    setAnnouncementsError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl('/api/ngo/announcements'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setAnnouncements(response.data.announcements || []);
        announcementsFetchedRef.current = true;
      } else {
        setAnnouncements([]);
        setAnnouncementsError('Unable to load announcements.');
        announcementsFetchedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching announcements:', error);
      setAnnouncements([]);
      setAnnouncementsError(error.response?.data?.error || 'Failed to fetch announcements.');
      announcementsFetchedRef.current = true;
    }
  }, [activeSection]);

  useEffect(() => {
    // Only fetch if we're on announcements section and haven't fetched yet
    if (activeSection === 'announcements' && !announcementsFetchedRef.current) {
      fetchAnnouncements();
    }
  }, [activeSection]);

  const fetchEvents = useCallback(async (forceRefresh = false) => {
    if (activeSection !== 'events') return;
    
    // If we already have events and it's not a forced refresh, don't fetch again
    if (eventsFetchedRef.current && !forceRefresh) {
      return;
    }
    
    // Never show loading - data loads in background
    setEventsError('');
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl('/api/ngo/events'),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setEvents(response.data.events || []);
        eventsFetchedRef.current = true;
      } else {
        setEvents([]);
        setEventsError('Unable to load events.');
        eventsFetchedRef.current = true;
      }
    } catch (error) {
      console.error('Error fetching events:', error);
      setEvents([]);
      setEventsError(error.response?.data?.error || 'Failed to fetch events.');
      eventsFetchedRef.current = true;
    }
  }, [activeSection]);

  const handleDeleteMember = async (memberId, memberName) => {
    if (!window.confirm(`Are you sure you want to remove ${memberName || 'this member'} from your NGO?`)) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.delete(
        buildApiUrl(`/api/ngo/members/${memberId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );

      // Refresh members list
      membersFetchedRef.current = false;
      await fetchMembers(true);
    } catch (error) {
      console.error('Error deleting NGO member:', error);
      const message = error.response?.data?.error || 'Failed to delete member. Please try again.';
      alert(message);
    }
  };

  useEffect(() => {
    // Only fetch if we're on events section and haven't fetched yet
    if (activeSection === 'events' && !eventsFetchedRef.current) {
      fetchEvents();
    }
  }, [activeSection]);

  const fetchMemberActivity = async (memberId) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl(`/api/ngo/members/${memberId}/activity`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        setMemberActivity(response.data);
        setShowActivityModal(true);
      }
    } catch (error) {
      console.error('Error fetching member activity:', error);
      alert('Failed to load member activity.');
    }
  };

  const filteredMembers = useMemo(() => {
    let filtered = members;
    
    // Apply search filter
    if (memberSearch) {
      const term = memberSearch.toLowerCase();
      filtered = filtered.filter(m =>
        (m.name || '').toLowerCase().includes(term) ||
        (m.membershipId || '').toLowerCase().includes(term) ||
        (m.sector || m.industry || '').toLowerCase().includes(term) ||
        (m.email || '').toLowerCase().includes(term) ||
        (m.memberType || '').toLowerCase().includes(term) ||
        (isBapsAfrica && (m.gender || '').toLowerCase().includes(term))
      );
    }
    
    return filtered;
  }, [members, memberSearch, isBapsAfrica]);

  // Get unique sectors and locations for filter dropdowns
  const uniqueSectors = useMemo(() => {
    const sectors = members.map(m => m.sector).filter(s => s);
    return [...new Set(sectors)].sort();
  }, [members]);

  const uniqueLocations = useMemo(() => {
    const locations = members.map(m => {
      const loc = m.location;
      if (!loc) return null;
      if (typeof loc === 'string') return loc;
      if (typeof loc === 'object') {
        // Convert location object to string
        const parts = [];
        if (loc.city) parts.push(loc.city);
        if (loc.country) parts.push(loc.country);
        if (loc.address) parts.push(loc.address);
        return parts.length > 0 ? parts.join(', ') : null;
      }
      return String(loc);
    }).filter(l => l);
    return [...new Set(locations)].sort();
  }, [members]);

  const handleDownloadTemplate = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        buildApiUrl('/api/bulk-import/template/association_members'),
        {
          headers: { Authorization: `Bearer ${token}` },
          responseType: 'blob'
        }
      );

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'association_members_template.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download template:', error);
      alert('Failed to download template. Please try again.');
    }
  };

  const handleBulkUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;

    try {
      setBulkUploading(true);
      setBulkUploadResult(null);
      const token = localStorage.getItem('token');
      const formData = new FormData();
      formData.append('file', file);
      formData.append('cohortLabel', 'Association Members Upload');

      const response = await axios.post(
        buildApiUrl('/api/ngo/bulk-members/upload'),
        formData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      setBulkUploadResult({
        success: response.data?.success,
        message: response.data?.message,
        successful_count: response.data?.successful_count || 0,
        skipped_count: response.data?.skipped_count || 0,
        failed_count: response.data?.failed_count || 0,
        emails_sent_count: response.data?.emails_sent_count || 0,
        emails_failed_count: response.data?.emails_failed_count || 0
      });

      // Force refresh members list after bulk upload
      membersFetchedRef.current = false;
      // Add a small delay to ensure backend has processed the new members
      setTimeout(() => {
        fetchMembers(true);
      }, 500);
      fetchDashboardData();
      
      // If not already on members section, optionally navigate there
      // (commented out - let user stay on bulk upload section to see results)
      // if (activeSection !== 'members') {
      //   setTimeout(() => {
      //     setActiveSection('members');
      //   }, 1000);
      // }
    } catch (error) {
      console.error('Association bulk upload failed:', error);
      let errorMessage = 'Bulk upload failed. Please check your file and try again.';
      
      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;
      }
      
      if (error.response?.data?.validation_errors && Array.isArray(error.response.data.validation_errors)) {
        const validationErrors = error.response.data.validation_errors.slice(0, 5).map(err => 
          `Row ${err.row || '?'}: ${err.error || err.message || 'Validation error'}`
        ).join('; ');
        errorMessage += ` Validation errors: ${validationErrors}${error.response.data.validation_errors.length > 5 ? '...' : ''}`;
      }
      
      setBulkUploadResult({
        success: false,
        message: errorMessage,
        successful_count: 0,
        skipped_count: 0,
        failed_count: error.response?.data?.validation_errors?.length || 0
      });
    } finally {
      setBulkUploading(false);
      event.target.value = '';
    }
  };

  const renderDashboard = () => (
    <div style={{ marginTop: '-1rem', paddingTop: '0' }}>
      <div className="stats-grid">
        <StatCard
          title="Total Members"
          value={dashboardData.stats.totalMembers}
          icon={faUsers}
          iconColor="#f97316"
        />
        <StatCard
          title="Onboarded Members"
          value={dashboardData.stats.onboardedMembers}
          icon={faBuilding}
          iconColor="#0d9488"
        />
        <StatCard
          title="Active Jobs"
          value={dashboardData.stats.activeJobsForMembers}
          icon={faBriefcase}
          iconColor="#3b82f6"
        />
        <StatCard
          title="Total Placements"
          value={dashboardData.stats.placements}
          icon={faChartBar}
          iconColor="#10b981"
        />
      </div>

      {/* Recent Activity & Overview */}
      <div style={{ 
        display: 'grid', 
        gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', 
        gap: '1.5rem',
        marginTop: '2rem'
      }}>
        {/* Recent Members */}
        <div className="section-card">
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faUsers} style={{ color: '#f97316' }} />
            Recent Members
          </h3>
          {members.length > 0 ? (
            <div>
              {members.slice(0, 5).map((member, index) => (
                <div
                  key={member.id || index}
                  style={{
                    padding: '0.75rem',
                    borderBottom: index < Math.min(4, members.length - 1) ? '1px solid #e5e7eb' : 'none',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                  }}
                >
                  <div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 600, color: '#0f172a' }}>{member.name || 'Unknown'}</span>
                      {member.memberVerificationStatus === 'verified' && member.memberVerificationType === 'ngo' && (
                        <CommunityMemberBadge type="ngo" size="small" />
                      )}
                    </div>
                    <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                      {member.sector || 'No sector'} • {(() => {
                        const loc = member.location;
                        if (!loc) return 'No location';
                        if (typeof loc === 'string') return loc;
                        if (typeof loc === 'object') {
                          const parts = [];
                          if (loc.city) parts.push(loc.city);
                          if (loc.country) parts.push(loc.country);
                          return parts.length > 0 ? parts.join(', ') : 'No location';
                        }
                        return String(loc);
                      })()}
                    </div>
                  </div>
                  {member.onboardedUsers > 0 && (
                    <span style={{
                      background: '#10b981',
                      color: 'white',
                      padding: '0.25rem 0.5rem',
                      borderRadius: '4px',
                      fontSize: '0.75rem',
                      fontWeight: 600
                    }}>
                      Onboarded
                    </span>
                  )}
                </div>
              ))}
              {members.length > 5 && (
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    setActiveSection('members');
                    handleNavClick({ section: 'members' });
                  }}
                  style={{ width: '100%', marginTop: '0.75rem' }}
                >
                  View All Members ({members.length})
                </button>
              )}
            </div>
          ) : (
            <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
              <FontAwesomeIcon icon={faUsers} style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
              <p>No members yet. Start by uploading members.</p>
            </div>
          )}
        </div>

        {/* Member Engagement Summary */}
        <div className="section-card">
          <h3 style={{ marginBottom: '1rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <FontAwesomeIcon icon={faChartBar} style={{ color: '#0d9488' }} />
            Member Engagement
          </h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.25rem' }}>
                <span style={{ color: '#64748b', fontSize: '0.875rem' }}>Onboarding Rate</span>
                <span style={{ fontWeight: 600, color: '#0f172a' }}>
                  {dashboardData.stats.totalMembers > 0
                    ? Math.round((dashboardData.stats.onboardedMembers / dashboardData.stats.totalMembers) * 100)
                    : 0}%
                </span>
              </div>
              <div style={{
                width: '100%',
                height: '8px',
                background: '#e5e7eb',
                borderRadius: '4px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${dashboardData.stats.totalMembers > 0
                    ? (dashboardData.stats.onboardedMembers / dashboardData.stats.totalMembers) * 100
                    : 0}%`,
                  height: '100%',
                  background: '#0d9488',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </div>
            <div style={{
              padding: '1rem',
              background: '#f0fdfa',
              borderRadius: '8px',
              border: '1px solid #99f6e4'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#0f766e', marginBottom: '0.25rem' }}>
                Active Jobs Available
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#0d9488' }}>
                {dashboardData.stats.activeJobsForMembers || 0}
              </div>
            </div>
            <div style={{
              padding: '1rem',
              background: '#fff7ed',
              borderRadius: '8px',
              border: '1px solid #fed7aa'
            }}>
              <div style={{ fontSize: '0.875rem', color: '#9a3412', marginBottom: '0.25rem' }}>
                Total Placements
              </div>
              <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#f97316' }}>
                {dashboardData.stats.placements || 0}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );

  const handleExportMembers = () => {
    try {
      const headers = isBapsAfrica 
        ? ['Name', 'Membership ID', 'Gender', 'Sector', 'Location', 'Primary Contact', 'Onboarded Users']
        : ['Name', 'Membership ID', 'Sector', 'Location', 'Primary Contact', 'Onboarded Users'];
      
      const csvContent = [
        headers.join(','),
        ...filteredMembers.map(member => {
          const baseFields = [
            `"${member.name || ''}"`,
            `"${member.membershipId || ''}"`
          ];
          if (isBapsAfrica) {
            baseFields.push(`"${member.gender || ''}"`);
          }
          const locationStr = (() => {
            const loc = member.location;
            if (!loc) return '';
            if (typeof loc === 'string') return loc;
            if (typeof loc === 'object') {
              const parts = [];
              if (loc.city) parts.push(loc.city);
              if (loc.country) parts.push(loc.country);
              if (loc.address) parts.push(loc.address);
              return parts.join(', ');
            }
            return String(loc);
          })();
          baseFields.push(
            `"${member.sector || ''}"`,
            `"${locationStr}"`,
            `"${member.primaryContact || ''}"`,
            member.onboardedUsers ?? 0
          );
          return baseFields.join(',');
        })
      ].join('\n');

      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const link = document.createElement('a');
      const url = URL.createObjectURL(blob);
      link.setAttribute('href', url);
      link.setAttribute('download', `association_members_${new Date().toISOString().split('T')[0]}.csv`);
      link.style.visibility = 'hidden';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error exporting members:', error);
      alert('Failed to export members. Please try again.');
    }
  };

  const renderMembers = () => (
    <div className="section-card association-table-section">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
        <h2>
          <FontAwesomeIcon icon={faUsers} /> Members
        </h2>
        {filteredMembers.length > 0 && (
          <button
            className="btn btn-secondary"
            onClick={handleExportMembers}
            style={{ 
              display: 'inline-flex', 
              alignItems: 'center', 
              gap: '0.5rem',
              padding: '8px 12px',
              fontSize: '13px',
              fontWeight: '600',
              whiteSpace: 'nowrap',
              width: 'auto',
              minWidth: 'auto'
            }}
          >
            <FontAwesomeIcon icon={faDownload} />
            Export CSV
          </button>
        )}
      </div>
      {/* Search and Filters */}
      <div style={{ marginBottom: '1rem' }}>
        <div className="filters" style={{ marginBottom: '0.75rem', display: 'flex' }}>
          <input
            type="text"
            className="filter-select"
            placeholder={
              isElafaNgo
                ? "Search by name or email"
                : (isBapsAfrica
                    ? "Search by name, email, membership ID, gender, or sector"
                    : "Search by name, email, membership ID, or sector")
            }
            value={memberSearch}
            onChange={(e) => setMemberSearch(e.target.value)}
            style={{ maxWidth: '500px', width: '100%' }}
          />
        </div>
        <div style={{ 
          display: 'flex', 
          flexDirection: 'row',
          gap: '0.75rem', 
          flexWrap: 'wrap', 
          alignItems: 'center'
        }}>
          <select
            className="filter-select"
            value={memberFilters.status}
            onChange={(e) => setMemberFilters({ ...memberFilters, status: e.target.value })}
            style={{ 
              minWidth: '120px', 
              width: 'auto',
              maxWidth: '200px',
              flexShrink: 0
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
          {!isElafaNgo && (
            <>
              <select
                className="filter-select"
                value={memberFilters.sector}
                onChange={(e) => setMemberFilters({ ...memberFilters, sector: e.target.value })}
                style={{ 
                  minWidth: '150px', 
                  width: 'auto',
                  maxWidth: '200px',
                  flexShrink: 0
                }}
              >
                <option value="">All Sectors</option>
                {uniqueSectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
              <select
                className="filter-select"
                value={memberFilters.location}
                onChange={(e) => setMemberFilters({ ...memberFilters, location: e.target.value })}
                style={{ 
                  minWidth: '150px', 
                  width: 'auto',
                  maxWidth: '200px',
                  flexShrink: 0
                }}
              >
                <option value="">All Locations</option>
                {uniqueLocations.map(location => (
                  <option key={location} value={location}>{location}</option>
                ))}
              </select>
            </>
          )}
          <select
            className="filter-select"
            value={memberFilters.activity}
            onChange={(e) => setMemberFilters({ ...memberFilters, activity: e.target.value })}
            style={{ 
              minWidth: '140px', 
              width: 'auto',
              maxWidth: '200px',
              flexShrink: 0
            }}
          >
            <option value="">All Activity</option>
            <option value="active">Active (Today)</option>
            <option value="recent">Recent (Last 7 days)</option>
            <option value="inactive">Inactive (30+ days)</option>
          </select>
          {(memberFilters.status || memberFilters.sector || memberFilters.location || memberFilters.activity) && (
            <button
              onClick={() => setMemberFilters({ status: '', sector: '', location: '', activity: '' })}
              className="btn btn-secondary"
              style={{ 
                padding: '8px 12px', 
                fontSize: '13px',
                flexShrink: 0,
                whiteSpace: 'nowrap'
              }}
            >
              <FontAwesomeIcon icon={faTimes} /> Clear Filters
            </button>
          )}
        </div>
      </div>
      {membersError && (
        <div className="alert danger" style={{ marginBottom: '1rem' }}>{membersError}</div>
      )}
      {filteredMembers.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">👥</div>
          <h3>No members found</h3>
          <p>Start by uploading your association members in bulk.</p>
        </div>
      ) : (
        <div className="applications-table-wrapper">
          <table className="applications-table ngo-members-table">
            <thead>
              <tr>
                <th>VOLUNTEER NAME</th>
                {isBapsAfrica && <th>GENDER</th>}
                <th>ACTIVITY</th>
                <th>JOBS</th>
                <th>APPLICATIONS</th>
                <th>ACTIONS</th>
              </tr>
            </thead>
            <tbody>
              {filteredMembers.map((member) => {
                const getActivityBadge = () => {
                  const status = member.activityStatus || 'never';
                  
                  if (status === 'active') {
                    return <span style={{ 
                      padding: '6px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      backgroundColor: '#10b981', 
                      color: 'white',
                      display: 'inline-block',
                      textTransform: 'uppercase'
                    }}>Active</span>;
                  } else if (status === 'recent') {
                    return <span style={{ 
                      padding: '6px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      backgroundColor: '#3b82f6', 
                      color: 'white',
                      display: 'inline-block',
                      textTransform: 'uppercase'
                    }}>Recent</span>;
                  } else if (status === 'inactive') {
                    return <span style={{ 
                      padding: '6px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      backgroundColor: '#f59e0b', 
                      color: 'white',
                      display: 'inline-block',
                      textTransform: 'uppercase'
                    }}>Inactive</span>;
                  } else if (status === 'dormant') {
                    return <span style={{ 
                      padding: '6px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      backgroundColor: '#ef4444', 
                      color: 'white',
                      display: 'inline-block',
                      textTransform: 'uppercase'
                    }}>Dormant</span>;
                  } else {
                    return <span style={{ 
                      padding: '6px 12px', 
                      borderRadius: '20px', 
                      fontSize: '12px', 
                      fontWeight: 600, 
                      backgroundColor: '#9ca3af', 
                      color: 'white',
                      display: 'inline-block',
                      textTransform: 'uppercase'
                    }}>Never</span>;
                  }
                };
                
                const getLastLoginText = () => {
                  if (!member.lastLogin) return 'Never logged in';
                  const days = member.daysSinceLogin;
                  if (days === 0) return 'Today';
                  if (days === 1) return 'Yesterday';
                  if (days < 7) return `${days} days ago`;
                  if (days < 30) return `${Math.floor(days / 7)} weeks ago`;
                  return `${Math.floor(days / 30)} months ago`;
                };
                
                return (
                  <tr key={member.id} className="application-row">
                    <td>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column',
                        gap: '4px'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '8px',
                          flexWrap: 'wrap'
                        }}>
                          <span style={{ 
                            fontWeight: 600, 
                            fontSize: '15px',
                            color: '#1f2937'
                          }}>
                            {member.name || 'Unknown'}
                          </span>
                          {member.memberVerificationStatus === 'verified' && member.memberVerificationType === 'ngo' && (
                            <CommunityMemberBadge type="ngo" size="small" />
                          )}
                        </div>
                        {member.email && (
                          <div style={{ 
                            fontSize: '13px', 
                            color: '#64748b',
                            fontWeight: 400
                          }}>
                            {member.email}
                          </div>
                        )}
                        {member.memberType === 'job_seeker' && (
                          <div style={{ 
                            fontSize: '11px', 
                            color: '#3b82f6', 
                            marginTop: '2px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px'
                          }}>
                            <FontAwesomeIcon icon={faUser} style={{ fontSize: '10px' }} />
                            Job Seeker
                          </div>
                        )}
                      </div>
                    </td>
                    {isBapsAfrica && (
                      <td>
                        <div style={{ 
                          color: '#64748b',
                          fontSize: '14px'
                        }}>
                          {member.gender || '—'}
                        </div>
                      </td>
                    )}
                    <td>
                      <div style={{ 
                        display: 'flex', 
                        flexDirection: 'column', 
                        gap: '6px'
                      }}>
                        <div style={{ 
                          display: 'flex',
                          alignItems: 'center',
                          gap: '6px'
                        }}>
                          <span style={{ 
                            fontSize: '12px', 
                            fontWeight: 600,
                            color: '#1f2937'
                          }}>
                            Active Session:
                          </span>
                          <span style={{ 
                            fontSize: '12px', 
                            fontWeight: 600,
                            color: member.activityStatus === 'active' ? '#10b981' : '#9ca3af'
                          }}>
                            {member.activityStatus === 'active' ? 'Yes' : 'No'}
                          </span>
                        </div>
                        {(() => {
                          // Parse lastLogin - handle ISO string, datetime object, or null
                          let lastLoginDate = null;
                          if (member.lastLogin) {
                            try {
                              // Handle ISO string format
                              if (typeof member.lastLogin === 'string') {
                                // Handle various ISO formats
                                let dateStr = member.lastLogin;
                                // Remove 'Z' if present and handle timezone
                                if (dateStr.endsWith('Z')) {
                                  dateStr = dateStr.slice(0, -1) + '+00:00';
                                }
                                // If no timezone, assume UTC
                                if (!dateStr.includes('+') && !dateStr.includes('-', 10)) {
                                  dateStr = dateStr + '+00:00';
                                }
                                lastLoginDate = new Date(dateStr);
                              } else if (member.lastLogin instanceof Date) {
                                lastLoginDate = member.lastLogin;
                              } else {
                                // Try to parse as date
                                lastLoginDate = new Date(member.lastLogin);
                              }
                              
                              // Validate the date
                              if (isNaN(lastLoginDate.getTime())) {
                                console.warn('Invalid lastLogin date for member:', member.name, member.email, 'lastLogin:', member.lastLogin);
                                lastLoginDate = null;
                              } else {
                                // Debug log for successful parsing
                                console.log('Parsed lastLogin for', member.name, ':', lastLoginDate.toISOString());
                              }
                            } catch (e) {
                              console.error('Error parsing lastLogin date for', member.name, ':', e, 'lastLogin value:', member.lastLogin);
                              lastLoginDate = null;
                            }
                          } else {
                            // Debug: log when lastLogin is missing
                            console.log('No lastLogin data for member:', member.name, member.email, 'member data:', {
                              lastLogin: member.lastLogin,
                              activityStatus: member.activityStatus,
                              daysSinceLogin: member.daysSinceLogin
                            });
                          }
                          
                          if (lastLoginDate) {
                            return (
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#64748b',
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '2px'
                              }}>
                                <div style={{ 
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '4px'
                                }}>
                                  <FontAwesomeIcon icon={faClock} style={{ fontSize: '10px' }} />
                                  <span>Last Active:</span>
                                </div>
                                <div style={{ 
                                  marginLeft: '14px',
                                  fontSize: '10px',
                                  color: '#6b7280'
                                }}>
                                  {lastLoginDate.toLocaleDateString('en-US', {
                                    month: 'short',
                                    day: 'numeric',
                                    year: 'numeric'
                                  })}
                                </div>
                                <div style={{ 
                                  marginLeft: '14px',
                                  fontSize: '10px',
                                  color: '#6b7280'
                                }}>
                                  {lastLoginDate.toLocaleTimeString('en-US', {
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true
                                  })}
                                </div>
                              </div>
                            );
                          } else {
                            return (
                              <div style={{ 
                                fontSize: '11px', 
                                color: '#9ca3af',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '4px'
                              }}>
                                <FontAwesomeIcon icon={faClock} style={{ fontSize: '10px' }} />
                                <span>Never logged in</span>
                              </div>
                            );
                          }
                        })()}
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ 
                        fontWeight: 600, 
                        color: '#3b82f6',
                        fontSize: '14px'
                      }}>
                        {member.jobCount || 0}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span style={{ 
                        fontWeight: 600, 
                        color: '#10b981',
                        fontSize: '14px'
                      }}>
                        {member.applicationCount || 0}
                      </span>
                    </td>
                    <td>
                      <div style={{
                        display: 'flex',
                        gap: '8px',
                        justifyContent: 'flex-start',
                        alignItems: 'center'
                      }}>
                        <button
                          onClick={() => fetchMemberActivity(member.id)}
                          style={{
                            padding: '8px 16px',
                            fontSize: '13px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                            backgroundColor: '#14b8a6',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#0d9488';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#14b8a6';
                          }}
                          title="View Activity"
                        >
                          <FontAwesomeIcon icon={faEye} />
                          View Details
                        </button>
                        <button
                          onClick={() => handleDeleteMember(member.id, member.name)}
                          style={{
                            padding: '8px 10px',
                            fontSize: '12px',
                            fontWeight: 600,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '4px',
                            backgroundColor: '#ef4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            transition: 'background-color 0.2s ease',
                            whiteSpace: 'nowrap'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#b91c1c';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = '#ef4444';
                          }}
                          title="Remove member"
                        >
                          <FontAwesomeIcon icon={faTimesCircle} />
                          Delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Member Activity Modal */}
      {showActivityModal && memberActivity && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '800px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
                Activity: {memberActivity.memberName}
              </h3>
              <button
                onClick={() => {
                  setShowActivityModal(false);
                  setMemberActivity(null);
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '1rem', marginBottom: '2rem' }}>
              <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Total Jobs Posted</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#3b82f6' }}>{memberActivity.totalJobs || 0}</div>
              </div>
              <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Total Applications</div>
                <div style={{ fontSize: '1.5rem', fontWeight: 700, color: '#10b981' }}>{memberActivity.totalApplications || 0}</div>
              </div>
              <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Last Login</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a' }}>
                  {memberActivity.lastLogin 
                    ? new Date(memberActivity.lastLogin).toLocaleString()
                    : 'Never'}
                </div>
              </div>
              <div style={{ padding: '1rem', background: '#f3f4f6', borderRadius: '8px' }}>
                <div style={{ fontSize: '0.875rem', color: '#64748b', marginBottom: '0.25rem' }}>Profile Status</div>
                <div style={{ fontSize: '1rem', fontWeight: 600, color: '#0f172a', display: 'flex', gap: '0.5rem', alignItems: 'center' }}>
                  {memberActivity.profileCompleted ? (
                    <><FontAwesomeIcon icon={faCheckCircle} style={{ color: '#10b981' }} /> Complete</>
                  ) : (
                    <><FontAwesomeIcon icon={faTimesCircle} style={{ color: '#ef4444' }} /> Incomplete</>
                  )}
                </div>
              </div>
            </div>

            {memberActivity.recentJobs && memberActivity.recentJobs.length > 0 && (
              <div style={{ marginBottom: '2rem' }}>
                <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600 }}>Recent Job Postings</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {memberActivity.recentJobs.slice(0, 5).map((job) => (
                    <div key={job.id} style={{
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{job.title}</div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          {job.createdAt ? new Date(job.createdAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        backgroundColor: job.status === 'active' ? '#10b981' : '#f59e0b',
                        color: 'white'
                      }}>
                        {job.status || '—'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {memberActivity.recentApplications && memberActivity.recentApplications.length > 0 && (
              <div>
                <h4 style={{ marginBottom: '1rem', fontSize: '1.1rem', fontWeight: 600 }}>Recent Applications Received</h4>
                <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                  {memberActivity.recentApplications.slice(0, 5).map((app) => (
                    <div key={app.id} style={{
                      padding: '0.75rem',
                      border: '1px solid #e5e7eb',
                      borderRadius: '8px',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}>
                      <div>
                        <div style={{ fontWeight: 600 }}>{app.applicantName || 'Unknown'}</div>
                        <div style={{ fontSize: '0.875rem', color: '#64748b' }}>
                          Applied for {app.jobTitle || 'Job'} • {app.createdAt ? new Date(app.createdAt).toLocaleDateString() : '—'}
                        </div>
                      </div>
                      <span style={{
                        padding: '4px 8px',
                        borderRadius: '12px',
                        fontSize: '11px',
                        fontWeight: 600,
                        backgroundColor: app.status === 'accepted' ? '#10b981' : app.status === 'rejected' ? '#ef4444' : '#3b82f6',
                        color: 'white'
                      }}>
                        {app.status || 'pending'}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {(!memberActivity.recentJobs || memberActivity.recentJobs.length === 0) && 
             (!memberActivity.recentApplications || memberActivity.recentApplications.length === 0) && (
              <div style={{ textAlign: 'center', padding: '2rem', color: '#9ca3af' }}>
                <FontAwesomeIcon icon={faClock} style={{ fontSize: '2rem', marginBottom: '0.5rem' }} />
                <p>No recent activity</p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderBulkUpload = () => (
    <div className="section-card">
      <h2>
        <FontAwesomeIcon icon={faUpload} /> Bulk Upload Members
      </h2>
      <p style={{ marginBottom: '1rem', color: '#64748b' }}>
        Upload a CSV or Excel file with your NGO members. We will automatically onboard them,
        link them to your NGO account, and send their login credentials via email.
      </p>
      <ul style={{ marginBottom: '1.5rem', color: '#64748b' }}>
        <li>
          Required columns: <strong>memberName</strong>, <strong>email</strong>,{' '}
          <strong>phone</strong>
          {isBapsAfrica && <span> (for BAPS Africa, also include <strong>gender</strong> - Male or Female)</span>}
        </li>
        <li>
          Recommended columns: <strong>membershipId</strong>, <strong>sector</strong>,{' '}
          <strong>location</strong>, <strong>primaryContactName</strong>
          {isBapsAfrica && <span>, <strong>gender</strong></span>}
        </li>
      </ul>
      <div style={{ display: 'flex', gap: '1rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
        <button
          className="btn btn-secondary"
          onClick={handleDownloadTemplate}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            width: 'auto',
            minWidth: 'auto'
          }}
        >
          <FontAwesomeIcon icon={faDownload} />
          Download CSV Template
        </button>
        <label 
          className="btn btn-upload" 
          style={{ 
            cursor: 'pointer', 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            width: 'auto',
            minWidth: 'auto'
          }}
        >
          <FontAwesomeIcon icon={faUpload} />
          {bulkUploading ? 'Uploading...' : 'Select File'}
          <input
            type="file"
            accept=".csv,.xls,.xlsx"
            onChange={handleBulkUpload}
            style={{ display: 'none' }}
            disabled={bulkUploading}
          />
        </label>
      </div>
      {bulkUploadResult && (
        <div style={{ marginTop: '1rem' }}>
          <div className={`alert ${bulkUploadResult.success ? 'success' : 'danger'}`}>
            <div style={{ marginBottom: '0.5rem' }}>
              {bulkUploadResult.message}
            </div>
            {bulkUploadResult.success && (
              <div style={{ fontSize: '0.875rem', marginTop: '0.5rem', color: '#059669' }}>
                {bulkUploadResult.successful_count > 0 && (
                  <div>✓ Successfully imported: {bulkUploadResult.successful_count} member(s)</div>
                )}
                {bulkUploadResult.skipped_count > 0 && (
                  <div>⚠ Skipped (duplicates): {bulkUploadResult.skipped_count} member(s)</div>
                )}
                {bulkUploadResult.failed_count > 0 && (
                  <div>✗ Failed: {bulkUploadResult.failed_count} member(s)</div>
                )}
                {bulkUploadResult.emails_sent_count > 0 && (
                  <div>📧 Credentials emails sent: {bulkUploadResult.emails_sent_count} member(s)</div>
                )}
                {bulkUploadResult.emails_failed_count > 0 && (
                  <div>⚠ Email sending failed: {bulkUploadResult.emails_failed_count} member(s)</div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );

  const renderAnalytics = () => {
    const stats = dashboardData.stats || {};
    const total = stats.totalMembers || 0;
    const onboarded = stats.onboardedMembers || 0;
    const notOnboarded = total - onboarded;
    const onboardingRate = total > 0 ? Math.round((onboarded / total) * 100) : 0;

    // Get sector distribution from members
    const sectorCounts = {};
    members.forEach(member => {
      const sector = member.sector || 'Not Specified';
      sectorCounts[sector] = (sectorCounts[sector] || 0) + 1;
    });
    const sectors = Object.keys(sectorCounts);
    const sectorValues = Object.values(sectorCounts);

    // Chart data for Overall Onboarding Rate (Bar Chart)
    const onboardingRateChartData = {
      labels: ['Onboarding Rate'],
      datasets: [
        {
          label: 'Onboarded',
          data: [onboardingRate],
          backgroundColor: 'rgba(13, 148, 136, 0.8)',
          borderColor: 'rgba(13, 148, 136, 1)',
          borderWidth: 2
        },
        {
          label: 'Not Onboarded',
          data: [100 - onboardingRate],
          backgroundColor: 'rgba(229, 231, 235, 0.8)',
          borderColor: 'rgba(229, 231, 235, 1)',
          borderWidth: 2
        }
      ]
    };

    const onboardingRateChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'top'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `${context.dataset.label}: ${context.parsed.y}%`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          max: 100,
          ticks: {
            callback: function(value) {
              return value + '%';
            }
          }
        }
      }
    };

    // Chart data for Member Status Distribution (Doughnut Chart)
    const memberStatusChartData = {
      labels: ['Onboarded', 'Not Onboarded'],
      datasets: [
        {
          data: [onboarded, notOnboarded],
          backgroundColor: [
            'rgba(13, 148, 136, 0.8)',
            'rgba(156, 163, 175, 0.8)'
          ],
          borderColor: [
            'rgba(13, 148, 136, 1)',
            'rgba(156, 163, 175, 1)'
          ],
          borderWidth: 2
        }
      ]
    };

    const memberStatusChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: true,
          position: 'right'
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              const label = context.label || '';
              const value = context.parsed || 0;
              const total = context.dataset.data.reduce((a, b) => a + b, 0);
              const percentage = total > 0 ? Math.round((value / total) * 100) : 0;
              return `${label}: ${value} (${percentage}%)`;
            }
          }
        }
      }
    };

    // Chart data for Members by Sector (Bar Chart)
    const sectorChartData = {
      labels: sectors.length > 0 ? sectors : ['No Data'],
      datasets: [
        {
          label: 'Members',
          data: sectors.length > 0 ? sectorValues : [0],
          backgroundColor: 'rgba(249, 115, 22, 0.8)',
          borderColor: 'rgba(249, 115, 22, 1)',
          borderWidth: 2
        }
      ]
    };

    const sectorChartOptions = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          display: false
        },
        tooltip: {
          callbacks: {
            label: function(context) {
              return `Members: ${context.parsed.y}`;
            }
          }
        }
      },
      scales: {
        y: {
          beginAtZero: true,
          ticks: {
            stepSize: 1
          }
        }
      }
    };

    return (
      <div>
        <div className="section-card">
          <h2>
            <FontAwesomeIcon icon={faChartBar} /> Engagement & Analytics
          </h2>
          <p style={{ marginBottom: '1.5rem', color: '#64748b', maxWidth: '720px' }}>
            High-level analytics for your association members. These charts use live data from your member database.
          </p>

          {/* Top metrics row */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
              gap: '1rem',
              marginBottom: '2rem'
            }}
          >
            <div className="card" style={{ padding: '1rem 1.25rem' }}>
              <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
                Total Members
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0f172a' }}>{total}</div>
            </div>
            <div className="card" style={{ padding: '1rem 1.25rem' }}>
              <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
                Onboarded Members
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#0d9488' }}>{onboarded}</div>
            </div>
            <div className="card" style={{ padding: '1rem 1.25rem' }}>
              <div style={{ fontSize: '0.8rem', textTransform: 'uppercase', color: '#6b7280', marginBottom: '0.25rem' }}>
                Onboarding Rate
              </div>
              <div style={{ fontSize: '1.8rem', fontWeight: 800, color: '#f97316' }}>{onboardingRate}%</div>
            </div>
          </div>

          {/* Overall Onboarding Rate Chart */}
          <div
            className="section-card"
            style={{
              padding: '1.5rem 1.75rem',
              marginBottom: '1.5rem'
            }}
          >
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
              Overall Onboarding Rate
            </h3>
            {total === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                <p style={{ fontSize: '0.9rem', margin: 0 }}>
                  No member data yet. This chart will update automatically as members are onboarded.
                </p>
              </div>
            ) : (
              <div style={{ height: '300px' }}>
                <Bar data={onboardingRateChartData} options={onboardingRateChartOptions} />
              </div>
            )}
          </div>

          {/* Member Status Distribution Chart */}
          <div
            className="section-card"
            style={{
              padding: '1.5rem 1.75rem',
              marginBottom: '1.5rem'
            }}
          >
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
              Member Status Distribution
            </h3>
            {total === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                <p style={{ fontSize: '0.9rem', margin: 0 }}>No member data yet.</p>
              </div>
            ) : (
              <div style={{ height: '300px' }}>
                <Doughnut data={memberStatusChartData} options={memberStatusChartOptions} />
              </div>
            )}
          </div>

          {/* Members by Sector Chart */}
          <div
            className="section-card"
            style={{
              padding: '1.5rem 1.75rem'
            }}
          >
            <h3 style={{ fontSize: '1.1rem', fontWeight: 700, marginBottom: '1rem', color: '#0f172a' }}>
              Members by Sector
            </h3>
            {sectors.length === 0 ? (
              <div style={{ padding: '2rem', textAlign: 'center', color: '#9ca3af' }}>
                <p style={{ fontSize: '0.9rem', margin: 0 }}>No sector data yet. Add sector information when uploading members.</p>
              </div>
            ) : (
              <div style={{ height: '300px' }}>
                <Bar data={sectorChartData} options={sectorChartOptions} />
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  const renderJobs = () => (
    <div className="section-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>
          <FontAwesomeIcon icon={faBriefcase} /> Job Postings for Members
        </h2>
        <button
          className="btn btn-primary"
          onClick={() => navigate('/recruiter-dashboard?section=post-job')}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            width: 'auto',
            minWidth: 'auto'
          }}
        >
          <FontAwesomeIcon icon={faBriefcase} />
          Post New Job
        </button>
      </div>
      <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
        Post job opportunities that are specifically available for your association members. These jobs will be visible to all onboarded members.
      </p>
      <div className="empty-state">
        <div className="empty-icon">💼</div>
        <h3>No jobs posted yet</h3>
        <p>Start by posting job opportunities for your members.</p>
      </div>
    </div>
  );

  const handleCreateAnnouncement = () => {
    setEditingAnnouncement(null);
    setAnnouncementForm({ title: '', message: '' });
    setShowAnnouncementModal(true);
  };

  const handleEditAnnouncement = (announcement) => {
    setEditingAnnouncement(announcement);
    setAnnouncementForm({
      title: announcement.title || '',
      message: announcement.message || ''
    });
    setShowAnnouncementModal(true);
  };

  const handleSaveAnnouncement = async () => {
    if (!announcementForm.title.trim() || !announcementForm.message.trim()) {
      alert('Please fill in both title and message.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingAnnouncement) {
        // Update existing announcement
        const response = await axios.put(
          buildApiUrl(`/api/ngo/announcements/${editingAnnouncement.id}`),
          announcementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success) {
          announcementsFetchedRef.current = false;
          fetchAnnouncements(true);
          setShowAnnouncementModal(false);
          setEditingAnnouncement(null);
          setAnnouncementForm({ title: '', message: '' });
        }
      } else {
        // Create new announcement
        const response = await axios.post(
          buildApiUrl('/api/ngo/announcements'),
          announcementForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success) {
          announcementsFetchedRef.current = false;
          fetchAnnouncements(true);
          setShowAnnouncementModal(false);
          setAnnouncementForm({ title: '', message: '' });
        }
      }
    } catch (error) {
      console.error('Error saving announcement:', error);
      alert(error.response?.data?.error || 'Failed to save announcement.');
    }
  };

  const handleDeleteAnnouncement = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        buildApiUrl(`/api/ngo/announcements/${announcementId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        announcementsFetchedRef.current = false;
        fetchAnnouncements(true);
      }
    } catch (error) {
      console.error('Error deleting announcement:', error);
      alert(error.response?.data?.error || 'Failed to delete announcement.');
    }
  };

  const renderAnnouncements = () => (
    <div className="section-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>
          <FontAwesomeIcon icon={faBullhorn} /> Announcements
        </h2>
        <button
          className="btn btn-primary"
          onClick={handleCreateAnnouncement}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            width: 'auto',
            minWidth: 'auto'
          }}
        >
          <FontAwesomeIcon icon={faBullhorn} />
          Create Announcement
        </button>
      </div>
      <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
        Send important announcements and updates to all your NGO members. Members will receive notifications for new announcements.
      </p>

      {/* Announcement Modal - portal to document.body so it's not cut off and doesn't flicker (same as Association Dashboard) */}
      {showAnnouncementModal && ReactDOM.createPortal(
        <div
          role="dialog"
          aria-modal="true"
          aria-labelledby="ngo-announcement-modal-title"
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            padding: '1rem',
            boxSizing: 'border-box',
            isolation: 'isolate'
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setShowAnnouncementModal(false);
              setEditingAnnouncement(null);
              setAnnouncementForm({ title: '', message: '' });
            }
          }}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '12px',
              width: '100%',
              maxWidth: '600px',
              maxHeight: 'calc(100vh - 2rem)',
              display: 'flex',
              flexDirection: 'column',
              boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.25)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ flexShrink: 0, padding: '1.5rem 1.5rem 0', display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
              <h3 id="ngo-announcement-modal-title" style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
                {editingAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
              </h3>
              <button
                onClick={() => {
                  setShowAnnouncementModal(false);
                  setEditingAnnouncement(null);
                  setAnnouncementForm({ title: '', message: '' });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', padding: '0 1.5rem 1.5rem' }}>
              <div style={{ marginBottom: '1rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Title *
                </label>
                <input
                  type="text"
                  value={announcementForm.title}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, title: e.target.value })}
                  placeholder="Enter announcement title"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Message *
                </label>
                <textarea
                  value={announcementForm.message}
                  onChange={(e) => setAnnouncementForm({ ...announcementForm, message: e.target.value })}
                  placeholder="Enter announcement message"
                  rows={6}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem',
                    resize: 'vertical',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end', flexShrink: 0 }}>
                <button
                  onClick={() => {
                    setShowAnnouncementModal(false);
                    setEditingAnnouncement(null);
                    setAnnouncementForm({ title: '', message: '' });
                  }}
                  className="btn btn-secondary"
                  style={{ padding: '8px 16px' }}
                >
                  Cancel
                </button>
                <button
                  onClick={handleSaveAnnouncement}
                  className="btn btn-primary"
                  style={{ padding: '8px 16px' }}
                >
                  <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
                  {editingAnnouncement ? 'Update' : 'Create'} Announcement
                </button>
              </div>
            </div>
          </div>
        </div>,
        document.body
      )}

      {announcementsError && (
        <div className="alert danger" style={{ marginBottom: '1rem' }}>{announcementsError}</div>
      )}

      {announcements.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📢</div>
          <h3>No announcements yet</h3>
          <p>Create your first announcement to communicate with all members.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {announcements.map((announcement) => (
            <div
              key={announcement.id}
              style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '1.5rem',
                backgroundColor: '#fff'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                <div style={{ flex: 1 }}>
                  <h3 style={{ margin: 0, marginBottom: '0.5rem', fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                    {announcement.title}
                  </h3>
                  <p style={{ margin: 0, color: '#64748b', fontSize: '0.875rem' }}>
                    {new Date(announcement.createdAt).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </p>
                </div>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button
                    onClick={() => handleEditAnnouncement(announcement)}
                    className="btn btn-secondary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem'
                    }}
                  >
                    <FontAwesomeIcon icon={faEdit} />
                    Edit
                  </button>
                  <button
                    onClick={() => handleDeleteAnnouncement(announcement.id)}
                    className="btn btn-secondary"
                    style={{
                      padding: '6px 12px',
                      fontSize: '13px',
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      color: '#ef4444',
                      borderColor: '#ef4444'
                    }}
                  >
                    <FontAwesomeIcon icon={faTrash} />
                    Delete
                  </button>
                </div>
              </div>
              <div style={{ color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap' }}>
                {announcement.message}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );

  const handleCreateEvent = () => {
    setEditingEvent(null);
    setEventForm({
      title: '',
      description: '',
      eventDate: '',
      eventTime: '',
      location: '',
      eventType: 'Meeting'
    });
    setShowEventModal(true);
  };

  const handleEditEvent = (event) => {
    setEditingEvent(event);
    const eventDate = event.eventDate ? event.eventDate.split('T')[0] : '';
    setEventForm({
      title: event.title || '',
      description: event.description || '',
      eventDate: eventDate,
      eventTime: event.eventTime || '',
      location: event.location || '',
      eventType: event.eventType || 'Meeting'
    });
    setShowEventModal(true);
  };

  const handleSaveEvent = async () => {
    if (!eventForm.title.trim() || !eventForm.eventDate) {
      alert('Please fill in title and event date.');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (editingEvent) {
        // Update existing event
        const response = await axios.put(
          buildApiUrl(`/api/ngo/events/${editingEvent.id}`),
          eventForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success) {
          eventsFetchedRef.current = false;
          fetchEvents(true);
          setShowEventModal(false);
          setEditingEvent(null);
          setEventForm({
            title: '',
            description: '',
            eventDate: '',
            eventTime: '',
            location: '',
            eventType: 'Meeting'
          });
        }
      } else {
        // Create new event
        const response = await axios.post(
          buildApiUrl('/api/ngo/events'),
          eventForm,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (response.data?.success) {
          eventsFetchedRef.current = false;
          fetchEvents(true);
          setShowEventModal(false);
          setEventForm({
            title: '',
            description: '',
            eventDate: '',
            eventTime: '',
            location: '',
            eventType: 'Meeting'
          });
        }
      }
    } catch (error) {
      console.error('Error saving event:', error);
      alert(error.response?.data?.error || 'Failed to save event.');
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!window.confirm('Are you sure you want to delete this event?')) {
      return;
    }

    try {
      const token = localStorage.getItem('token');
      const response = await axios.delete(
        buildApiUrl(`/api/ngo/events/${eventId}`),
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (response.data?.success) {
        eventsFetchedRef.current = false;
        fetchEvents(true);
      }
    } catch (error) {
      console.error('Error deleting event:', error);
      alert(error.response?.data?.error || 'Failed to delete event.');
    }
  };

  const renderEvents = () => (
    <div className="section-card">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
        <h2>
          <FontAwesomeIcon icon={faCalendarAlt} /> Association Events
        </h2>
        <button
          className="btn btn-primary"
          onClick={handleCreateEvent}
          style={{ 
            display: 'inline-flex', 
            alignItems: 'center', 
            gap: '0.5rem',
            padding: '8px 12px',
            fontSize: '13px',
            fontWeight: '600',
            whiteSpace: 'nowrap',
            width: 'auto',
            minWidth: 'auto'
          }}
        >
          <FontAwesomeIcon icon={faCalendarAlt} />
          Create Event
        </button>
      </div>
      <p style={{ color: '#64748b', marginBottom: '1.5rem' }}>
        Manage association events, meetings, webinars, and networking sessions. Members can RSVP and receive event reminders.
      </p>

      {/* Event Modal */}
      {showEventModal && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.5)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 1000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '2rem',
            width: '90%',
            maxWidth: '600px',
            maxHeight: '90vh',
            overflow: 'auto'
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
              <h3 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700 }}>
                {editingEvent ? 'Edit Event' : 'Create Event'}
              </h3>
              <button
                onClick={() => {
                  setShowEventModal(false);
                  setEditingEvent(null);
                  setEventForm({
                    title: '',
                    description: '',
                    eventDate: '',
                    eventTime: '',
                    location: '',
                    eventType: 'Meeting'
                  });
                }}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '1.5rem',
                  cursor: 'pointer',
                  color: '#64748b'
                }}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Event Title *
              </label>
              <input
                type="text"
                value={eventForm.title}
                onChange={(e) => setEventForm({ ...eventForm, title: e.target.value })}
                placeholder="Enter event title"
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem'
                }}
              />
            </div>

            <div style={{ marginBottom: '1rem' }}>
              <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                Description
              </label>
              <textarea
                value={eventForm.description}
                onChange={(e) => setEventForm({ ...eventForm, description: e.target.value })}
                placeholder="Enter event description"
                rows={4}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  fontSize: '1rem',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Event Date *
                </label>
                <input
                  type="date"
                  value={eventForm.eventDate}
                  onChange={(e) => setEventForm({ ...eventForm, eventDate: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Event Time
                </label>
                <input
                  type="time"
                  value={eventForm.eventTime}
                  onChange={(e) => setEventForm({ ...eventForm, eventTime: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginBottom: '1rem' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Location
                </label>
                <input
                  type="text"
                  value={eventForm.location}
                  onChange={(e) => setEventForm({ ...eventForm, location: e.target.value })}
                  placeholder="Event location"
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem'
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '0.5rem', fontWeight: 600 }}>
                  Event Type
                </label>
                <select
                  value={eventForm.eventType}
                  onChange={(e) => setEventForm({ ...eventForm, eventType: e.target.value })}
                  style={{
                    width: '100%',
                    padding: '0.75rem',
                    borderRadius: '8px',
                    border: '1px solid #d1d5db',
                    fontSize: '1rem'
                  }}
                >
                  <option value="Meeting">Meeting</option>
                  <option value="Webinar">Webinar</option>
                  <option value="Networking">Networking</option>
                  <option value="Workshop">Workshop</option>
                  <option value="Conference">Conference</option>
                  <option value="Other">Other</option>
                </select>
              </div>
            </div>

            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'flex-end' }}>
              <button
                onClick={() => {
                  setShowEventModal(false);
                  setEditingEvent(null);
                  setEventForm({
                    title: '',
                    description: '',
                    eventDate: '',
                    eventTime: '',
                    location: '',
                    eventType: 'Meeting'
                  });
                }}
                className="btn btn-secondary"
                style={{ padding: '8px 16px' }}
              >
                Cancel
              </button>
              <button
                onClick={handleSaveEvent}
                className="btn btn-primary"
                style={{ padding: '8px 16px' }}
              >
                <FontAwesomeIcon icon={faSave} style={{ marginRight: '0.5rem' }} />
                {editingEvent ? 'Update' : 'Create'} Event
              </button>
            </div>
          </div>
        </div>
      )}

      {eventsError && (
        <div className="alert danger" style={{ marginBottom: '1rem' }}>{eventsError}</div>
      )}

      {events.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">📅</div>
          <h3>No events scheduled</h3>
          <p>Create your first event to engage with your members.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
          {events.map((event) => {
            const eventDate = new Date(event.eventDate);
            const isPast = eventDate < new Date();
            
            return (
              <div
                key={event.id}
                style={{
                  border: '1px solid #e5e7eb',
                  borderRadius: '8px',
                  padding: '1.5rem',
                  backgroundColor: '#fff',
                  opacity: isPast ? 0.7 : 1
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '1rem' }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
                      <h3 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#0f172a' }}>
                        {event.title}
                      </h3>
                      <span style={{
                        padding: '0.25rem 0.75rem',
                        borderRadius: '12px',
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        backgroundColor: '#f0fdf4',
                        color: '#166534'
                      }}>
                        {event.eventType}
                      </span>
                      {isPast && (
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 600,
                          backgroundColor: '#fef2f2',
                          color: '#991b1b'
                        }}>
                          Past
                        </span>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '0.25rem', color: '#64748b', fontSize: '0.875rem' }}>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        {eventDate.toLocaleDateString('en-US', {
                          year: 'numeric',
                          month: 'long',
                          day: 'numeric'
                        })}
                        {event.eventTime && ` at ${event.eventTime}`}
                      </div>
                      {event.location && (
                        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                          <FontAwesomeIcon icon={faMapMarkerAlt} />
                          {event.location}
                        </div>
                      )}
                    </div>
                  </div>
                  <div style={{ display: 'flex', gap: '0.5rem' }}>
                    <button
                      onClick={() => handleEditEvent(event)}
                      className="btn btn-secondary"
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem'
                      }}
                    >
                      <FontAwesomeIcon icon={faEdit} />
                      Edit
                    </button>
                    <button
                      onClick={() => handleDeleteEvent(event.id)}
                      className="btn btn-secondary"
                      style={{
                        padding: '6px 12px',
                        fontSize: '13px',
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '0.5rem',
                        color: '#ef4444',
                        borderColor: '#ef4444'
                      }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                      Delete
                    </button>
                  </div>
                </div>
                {event.description && (
                  <div style={{ color: '#374151', lineHeight: '1.6', whiteSpace: 'pre-wrap', marginTop: '1rem' }}>
                    {event.description}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  const renderSettings = () => {
    return <AssociationSettings />;
  };

  if (!user) {
    return (
      <div className="loading-container">
        <ThemedLoadingSpinner message="Loading your dashboard..." />
      </div>
    );
  }

  // Only show loading spinner for dashboard section, not for other sections
  // Other sections load fast (frontend) while data loads in background
  if (loading && activeSection === 'dashboard') {
    return (
      <div className="loading-container">
        <ThemedLoadingSpinner message="Loading association dashboard..." />
      </div>
    );
  }

  return (
    <>
      <AssociationDashboardHeader currentPage={getSectionTitle()} />
      <div className="university-dashboard-container ngo-dashboard-container">
        <aside className={`sidebar ${sidebarOpen ? 'sidebar-open' : 'sidebar-closed'}`}>
          <nav className="nav-menu" style={{ marginTop: '1rem' }}>
            {navigationItems.map(item => (
              <div
                key={item.id}
                className={`nav-item ${activeSection === item.section ? 'active' : ''}`}
                onClick={() => handleNavClick(item)}
              >
                <FontAwesomeIcon icon={item.icon} />
                <span>{item.label}</span>
                {item.badge && <span className="badge">{item.badge}</span>}
              </div>
            ))}
          </nav>

          {/* Profile Completion Card */}
          <div className="sidebar-card" style={{ margin: '20px', padding: '16px', borderRadius: '12px', background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.2)' }}>
            <div style={{ fontSize: '16px', fontWeight: 600, marginBottom: '8px', color: '#fff' }}>
              Profile Complete
            </div>
            <div style={{ fontSize: '30px', fontWeight: 700, color: '#fff' }}>
              {Math.max(0, Math.min(100, dashboardData.profileCompletion || 0))}%
            </div>
          </div>
        </aside>

        <main className="main-content">
          <div className="content-area">
            {activeSection === 'dashboard' && renderDashboard()}
            {activeSection === 'members' && renderMembers()}
            {activeSection === 'jobs' && renderJobs()}
            {activeSection === 'announcements' && renderAnnouncements()}
            {activeSection === 'events' && renderEvents()}
            {activeSection === 'bulk-upload' && renderBulkUpload()}
            {activeSection === 'analytics' && renderAnalytics()}
            {activeSection === 'settings' && renderSettings()}
          </div>
        </main>
      </div>
    </>
  );
};

export default NGODashboard;


